import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Settings, APP_CONFIG } from '../config/settings';
import { IdentityService, Identity } from './identity.service';
import { combine } from '../shared/utilities/route';
import { LocalStorageKeys } from './enums/storage-keys.enum';
import { Router } from '@angular/router';
import { WINDOW } from 'src/app/core/core.module';
import { LoggerService } from 'src/app/shared/services/logger.service';

export interface CoreToken {
    access_token: string;
    refresh_token: string;
    expires_in: number;
}

const REDIRECT_KEY = 'aya-portal.loginRedirect';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private identUrl: string;
    private coreAuthUrl: string;
    private _coreToken: CoreToken;

    constructor(
        private readonly _http: HttpClient,
        private readonly _identityService: IdentityService,
        private readonly _router: Router,
        private readonly _loggerService: LoggerService,
        @Inject(WINDOW) private readonly _window: Window,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {
        this.identUrl = combine(this._settings.coreAPIUrl, 'ayanova/account/users/me');
        this.coreAuthUrl = combine(this._settings.coreAPIUrl, 'connect/token');
    }

    get accessTokenSilentRefreshAvailableDate(): Date | null {
        const item = this._window.localStorage.getItem(LocalStorageKeys.SILENT_REFRESH_AVAILABLE_DATE);

        if (item) {
            return new Date(item);
        }

        return null;
    }

    private set accessTokenSilentRefreshAvailableDate(date: Date) {
        this._window.localStorage.setItem(LocalStorageKeys.SILENT_REFRESH_AVAILABLE_DATE, date.toISOString());
    }

    get accessTokenExpiryDate(): Date | null {
        const item = this._window.localStorage.getItem(LocalStorageKeys.TOKEN_EXPIRES_IN);

        if (item) {
            return new Date(item);
        }

        return null;
    }

    private set accessTokenExpiryDate(date: Date | null) {
        this._window.localStorage.setItem(LocalStorageKeys.TOKEN_EXPIRES_IN, date.toISOString());
    }

    get coreToken(): CoreToken {
        return this._coreToken;
    }

    set coreToken(token: CoreToken) {
        this._coreToken = token;
    }

    setLoginRedirect(url: string) {
        this._window.localStorage.setItem(REDIRECT_KEY, url);
    }

    signIn(username: string, password: string): Observable<CoreToken> {
        const authHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };

        const searchParams = new URLSearchParams();
        searchParams.append('username', username.toLowerCase());
        searchParams.append('password', password);
        searchParams.append('grant_type', 'password');
        searchParams.append('scope', 'openid email phone profile offline_access roles');
        searchParams.append('resource', window.location.origin);

        const requestBody = searchParams.toString();

        return this._http.post<CoreToken>(this.coreAuthUrl, requestBody, authHeaders).pipe(
            tap((token) => {
                this.coreToken = token;
                this._identityService.coreAccessToken = token.access_token;
                this._identityService.refreshToken = token.refresh_token;
                this._identityService.tokenExpiresIn = token.expires_in;
                this._setExpirations(token.expires_in);
            })
        );
    }

    retrieveIdentity(): Observable<string> {
        const params = this._identityService.enforceOAuth
            ? undefined
            : { headers: { Authorization: 'Bearer ' + this.coreToken.access_token } };
        return this._http.get<Identity>(this.identUrl, params).pipe(
            map((identity) => {
                if (identity.permissionsToken) {
                    if (this._identityService.enforceOAuth) {
                        this._identityService.set(identity);
                        return '';
                    }

                    identity.coreAccessToken = this.coreToken.access_token;
                    identity.coreRefreshToken = this.coreToken.refresh_token;
                    identity.expiresIn = this.coreToken.expires_in;

                    let startingPath = null;
                    const loginRedirect = this._window.localStorage.getItem(REDIRECT_KEY);

                    if (loginRedirect) {
                        this._window.localStorage.removeItem(REDIRECT_KEY);
                        startingPath = loginRedirect;
                    } else {
                        startingPath =
                            identity.defaultPage && identity.defaultPage.length
                                ? identity.defaultPage.replace('.', '/')
                                : 'welcome/home';
                    }

                    this._identityService.set(identity);
                    return startingPath.toLowerCase();
                }

                throw new Error('There are no modules available for your account.');
            }),
            catchError((error: unknown) => {
                return throwError(() => error);
            })
        );
    }

    signOut() {
        this._identityService.delete();
        this._router.navigate(['/signin'], { replaceUrl: true });
    }

    refreshToken(refreshTokenInProgress$?: BehaviorSubject<boolean>): Observable<CoreToken> {
        if (!this._identityService.refreshToken) {
            refreshTokenInProgress$?.next(false);
            this._identityService.isAccessTokenSilentRefreshLocked = false;
            this._loggerService.info('Token refresh is unlocked.');

            this._router.navigate(['/signin'], { replaceUrl: true });
            return EMPTY;
        }

        const authHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
        const query =
            'refresh_token=' +
            this._identityService.refreshToken +
            '&grant_type=refresh_token&scope=openid email phone profile offline_access roles';

        return this._http.post<CoreToken>(this.coreAuthUrl, query, authHeaders).pipe(
            map((response: CoreToken) => {
                const currentIdent = this._identityService.current;

                if (response?.access_token) {
                    this.coreToken = response;
                    this._identityService.coreAccessToken = response.access_token;
                    this._identityService.refreshToken = response.refresh_token;
                    this._identityService.tokenExpiresIn = response.expires_in;
                    currentIdent.coreAccessToken = response.access_token;
                    currentIdent.coreRefreshToken = response.refresh_token;
                    currentIdent.expiresIn = response.expires_in;

                    this._setExpirations(response.expires_in);
                    this._identityService.set(currentIdent);
                } else if (!currentIdent) {
                    this.signOut();
                    throw new Error('Current identity is empty.');
                } else {
                    this.signOut();
                    throw new Error('Access token was empty upon refresh.');
                }

                return response;
            }),
            catchError((error: unknown) => {
                const err: HttpErrorResponse = error as HttpErrorResponse;
                if (err?.status === HttpStatusCode.BadRequest) {
                    this.signOut();
                    return EMPTY;
                } else {
                    return throwError(() => new Error(err.error));
                }
            }),
            finalize(() => {
                // ensures token refresh doesn't remain locked if an error occurs
                // this method can be called by interceptor which will account
                // for any situation where we somehow didn't unlock when attempting
                // to refresh proactively (see TokenRefreshService)
                this._identityService.isAccessTokenSilentRefreshLocked = false;
                refreshTokenInProgress$?.next(false);
                this._loggerService.info('Token is unlocked');
            })
        );
    }

    isAuthenticated(): boolean {
        return this._identityService.exists();
    }

    private _setExpirations(accessExpiresIn: number): void {
        const now = new Date();
        this.accessTokenExpiryDate = this._calcAccessTokenExpiryDate(now, accessExpiresIn);
        this.accessTokenSilentRefreshAvailableDate = this._calcAccessTokenSilentRefreshAvailableDate(
            now,
            accessExpiresIn
        );
    }

    private _calcAccessTokenExpiryDate(start: Date, accessTokenExpiresInSeconds: number): Date {
        return this._calcDateSecondsFromStart(start, accessTokenExpiresInSeconds);
    }

    private _calcAccessTokenSilentRefreshAvailableDate(start: Date, accessTokenExpiresInSeconds: number): Date {
        const accessTokenSilentRefreshAvailableInSeconds =
            (this._settings.silentRefreshTokenLifespanElapsedPercentage / 100) * accessTokenExpiresInSeconds;

        return this._calcDateSecondsFromStart(
            start,
            accessTokenSilentRefreshAvailableInSeconds || accessTokenExpiresInSeconds
        );
    }

    private _calcDateSecondsFromStart(start: Date, seconds: number): Date {
        const clone = new Date(start);
        clone.setSeconds(start.getSeconds() + (seconds ?? 0));

        return clone;
    }
}
